import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { BrandSectionLayout } from './BrandSectionLayout';
import { EditableText } from '../ui/EditableText';
import { Upload, X } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';

interface MoodboardGallerySectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const MoodboardGallerySection: React.FC<MoodboardGallerySectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);

  // Parse gallery images from JSON string
  const galleryImages = section.content.galleryImages
    ? (typeof section.content.galleryImages === 'string'
        ? JSON.parse(section.content.galleryImages)
        : section.content.galleryImages)
    : [];

  const handleAddImage = () => {
    setCurrentImageIndex(galleryImages.length);
    setShowImageModal(true);
  };

  const handleReplaceImage = (index: number) => {
    setCurrentImageIndex(index);
    setShowImageModal(true);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = galleryImages.filter((_: string, i: number) => i !== index);
    updateSectionContent('galleryImages', JSON.stringify(newImages));
  };

  const handleImageSelect = (imageUrl: string) => {
    if (currentImageIndex !== null) {
      const newImages = [...galleryImages];
      if (currentImageIndex >= newImages.length) {
        newImages.push(imageUrl);
      } else {
        newImages[currentImageIndex] = imageUrl;
      }
      updateSectionContent('galleryImages', JSON.stringify(newImages));
    }
    setShowImageModal(false);
    setCurrentImageIndex(null);
  };

  // Bento grid layout patterns based on number of images
  const getBentoGridClass = (index: number, total: number) => {
    if (total === 2) {
      // Pattern: Two equal images side by side
      return 'col-span-1 row-span-1';
    } else if (total === 3) {
      // Pattern: Left large, right 2 stacked
      if (index === 0) return 'col-span-1 row-span-2'; // Left full height
      if (index === 1) return 'col-span-1 row-span-1'; // Right top
      if (index === 2) return 'col-span-1 row-span-1'; // Right bottom
    } else if (total === 4) {
      // Pattern: Left large, right has 1 large top + 2 small bottom
      if (index === 0) return 'col-span-1 row-span-2'; // Left full height
      if (index === 1) return 'col-span-2 row-span-1'; // Right top (spans 2 cols)
      if (index === 2) return 'col-span-1 row-span-1'; // Right bottom left
      if (index === 3) return 'col-span-1 row-span-1'; // Right bottom right
    } else if (total === 5) {
      // Pattern: 3 columns - left 2 stacked, middle 2 stacked, right 1 large (explicit positioning)
      if (index === 0) return 'col-start-1 row-start-1'; // Left top
      if (index === 1) return 'col-start-1 row-start-2'; // Left bottom
      if (index === 2) return 'col-start-2 row-start-1'; // Middle top
      if (index === 3) return 'col-start-2 row-start-2'; // Middle bottom
      if (index === 4) return 'col-start-3 row-start-1 row-span-2'; // Right full height
    } else if (total === 6) {
      // Pattern: Top row: Large + 2 small, Bottom row: 2 small + Large
      if (index === 0) return 'col-span-2 row-span-1'; // Top left large
      if (index === 1) return 'col-span-1 row-span-1'; // Top middle right
      if (index === 2) return 'col-span-1 row-span-1'; // Top far right
      if (index === 3) return 'col-span-1 row-span-1'; // Bottom left
      if (index === 4) return 'col-span-1 row-span-1'; // Bottom middle left
      if (index === 5) return 'col-span-2 row-span-1'; // Bottom right large
    } else if (total === 7) {
      // Pattern: 4 cols x 4 rows - Top: tall, wide, 2 small stacked | Bottom: 2 tall, wide
      if (index === 0) return 'col-start-1 row-start-1 row-span-2'; // Top left tall
      if (index === 1) return 'col-start-2 col-span-2 row-start-1 row-span-2'; // Top middle wide
      if (index === 2) return 'col-start-4 row-start-1'; // Top right small (top)
      if (index === 3) return 'col-start-4 row-start-2'; // Top right small (bottom)
      if (index === 4) return 'col-start-1 row-start-3 row-span-2'; // Bottom left tall
      if (index === 5) return 'col-start-2 row-start-3 row-span-2'; // Bottom middle tall
      if (index === 6) return 'col-start-3 col-span-2 row-start-3 row-span-2'; // Bottom right wide
    } else if (total === 8) {
      // Pattern: Top 2 squares, tall spans 2 rows, right stacked | Middle: wide spans 2 cols, tall continues, right stacked | Bottom: tall ends, 2 squares
      if (index === 0) return 'col-start-1 row-start-1'; // Top left
      if (index === 1) return 'col-start-2 row-start-1'; // Top middle left
      if (index === 2) return 'col-start-3 row-start-1 row-span-2'; // Tall (spans rows 1-2)
      if (index === 3) return 'col-start-4 row-start-1'; // Top right
      if (index === 4) return 'col-start-4 row-start-2'; // Middle right
      if (index === 5) return 'col-start-1 col-span-2 row-start-2'; // Wide (spans cols 1-2)
      if (index === 6) return 'col-start-3 row-start-3'; // Bottom middle
      if (index === 7) return 'col-start-4 row-start-3'; // Bottom right
    } else if (total === 9) {
      // Pattern: 3x3 grid
      return 'col-span-1 row-span-1';
    } else if (total === 10) {
      // Pattern: Mixed with some larger
      if (index === 0 || index === 5) return 'col-span-2 row-span-1';
      return 'col-span-1 row-span-1';
    } else {
      // Default for other counts
      return 'col-span-1 row-span-1';
    }
  };

  // Get grid columns based on total images
  const getGridCols = (total: number) => {
    if (total === 2) return 'grid-cols-2';
    if (total === 3) return 'grid-cols-2';
    if (total === 4) return 'grid-cols-3';
    if (total === 5) return 'grid-cols-3';
    if (total === 6) return 'grid-cols-4';
    if (total === 7) return 'grid-cols-4';
    if (total === 8) return 'grid-cols-4';
    return 'grid-cols-4'; // Default for 9+
  };

  // Get grid rows based on total images
  const getGridRows = (total: number) => {
    if (total === 7) return 'grid-rows-4'; // 7 images needs 4 rows
    if (total === 8) return 'grid-rows-3'; // 8 images needs 3 rows
    return 'grid-rows-2'; // Default 2 rows
  };

  // Get add button position - always on the right side
  const getAddButtonClass = (total: number) => {
    // Determine the rightmost column based on the grid
    if (total <= 1) return 'col-start-2 row-start-1'; // 2 columns
    if (total === 2) return 'col-start-2 row-start-2'; // 2 columns, second row
    if (total === 3) return 'col-start-2 row-start-2'; // 2 columns, second row
    if (total === 4 || total === 5) return 'col-start-3 row-start-2'; // 3 columns, second row
    // For 6+ images, always use column 4 (rightmost)
    return 'col-start-4 row-start-1'; // 4 columns, top row
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      <div className="h-full w-full">
        {/* Bento Grid Gallery */}
        {galleryImages.length > 0 ? (
          <div className={`grid ${getGridCols(galleryImages.length)} ${getGridRows(galleryImages.length)} gap-4 h-full w-full`}>
            {galleryImages.map((image: string, index: number) => (
              <div
                key={index}
                className={`relative group overflow-hidden rounded-lg ${getBentoGridClass(index, galleryImages.length)}`}
              >
                <img
                  src={image}
                  alt={`Moodboard image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {isEditing && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleReplaceImage(index)}
                      className="px-3 py-1.5 bg-white text-gray-900 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Add More Button */}
            {isEditing && galleryImages.length < 10 && (
              <button
                onClick={handleAddImage}
                className={`border-2 border-dashed rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors ${
                  isGlobalDarkMode
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                } ${getAddButtonClass(galleryImages.length)}`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Upload className={`w-8 h-8 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Add Image
                  </span>
                </div>
              </button>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            {isEditing ? (
              <button
                onClick={handleAddImage}
                className={`px-6 py-4 border-2 border-dashed rounded-lg flex flex-col items-center gap-3 hover:border-gray-400 transition-colors ${
                  isGlobalDarkMode
                    ? 'border-gray-700 bg-gray-800/50'
                    : 'border-gray-300 bg-gray-50'
                }`}
              >
                <Upload className={`w-12 h-12 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className={`text-base ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Add moodboard images (5-10 recommended)
                </span>
              </button>
            ) : (
              <p className="text-gray-400">No images added yet</p>
            )}
          </div>
        )}
      </div>

      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setCurrentImageIndex(null);
        }}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Add Moodboard Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp']}
        maxFileSize={10}
        showUnsplash={true}
      />
    </BrandSectionLayout>
  );
};
